import { DeviceGroupData } from '@zspace/types';
import { useMemo } from 'react';
import { Element } from 'react-bulma-components';
import Button from '../../ui/button/button';
import Modal from '../../ui/modal/modal';

const WARNING_CONFIRMATION_TEXT = 'Are you sure you want to continue?';
const CANCEL_BUTTON_TEXT = 'No, cancel';

export type RemoveDevicesModalContentProps = {
  deviceGroupsToBeEmptied: DeviceGroupData[];
  onRemoveDevicesFromDeviceGroup: () => void;
  onCancel: () => void;
  loading: boolean;
  confirmationButtonText: string;
};

export function RemoveDevicesModalContent({
  deviceGroupsToBeEmptied,
  onRemoveDevicesFromDeviceGroup,
  onCancel,
  loading,
  confirmationButtonText,
}: RemoveDevicesModalContentProps) {
  const groupNames = useMemo(
    () => deviceGroupsToBeEmptied.map((group) => group.name),
    [deviceGroupsToBeEmptied]
  );

  return (
    <Element
      paddingless
      display="flex"
      flexDirection="column"
      className="gap-8"
    >
      <p>
        By removing the selected device(s) from{' '}
        <span className="has-text-weight-bold">{groupNames.join(', ')}</span>{' '}
        you will no longer be able to see
        {deviceGroupsToBeEmptied.length === 1 ? ' it' : ' them'}.
      </p>
      <p>
        If you want to add new devices to
        {deviceGroupsToBeEmptied.length === 1 ? ' it' : ' them'}, you will have
        to do it using the group code.
      </p>
      <p>{WARNING_CONFIRMATION_TEXT}</p>

      <Element display="flex" justifyContent="space-between">
        <Button color="primary-dark" outlined onClick={onCancel}>
          {CANCEL_BUTTON_TEXT}
        </Button>
        <Button
          color="primary-dark"
          onClick={onRemoveDevicesFromDeviceGroup}
          loading={loading}
        >
          <span>{confirmationButtonText}</span>
        </Button>
      </Element>
    </Element>
  );
}

export type RemoveDevicesModalProps = {
  show: boolean;
  deviceGroupsToBeEmptied: DeviceGroupData[];
  onRemoveDevicesFromDeviceGroup: () => void;
  onCancel: () => void;
  loading: boolean;
  title: string;
  confirmationButtonText: string;
};

export function RemoveDevicesModal({
  show,
  deviceGroupsToBeEmptied,
  onRemoveDevicesFromDeviceGroup,
  onCancel,
  loading,
  title,
  confirmationButtonText,
}: RemoveDevicesModalProps) {
  return (
    <Modal show={show} title={title} titleClassName="pb-6">
      <RemoveDevicesModalContent
        deviceGroupsToBeEmptied={deviceGroupsToBeEmptied}
        onRemoveDevicesFromDeviceGroup={onRemoveDevicesFromDeviceGroup}
        onCancel={onCancel}
        loading={loading}
        confirmationButtonText={confirmationButtonText}
      />
    </Modal>
  );
}

export default RemoveDevicesModal;
